/**
 * Shared validation and error parsing for site modals.
 */

const USER_FRIENDLY_MESSAGES: Record<string, string> = {
  "This field may not be blank.": "This field is required.",
  "This field is required.": "This field is required.",
  "This field may not be null.": "This field is required.",
  "A valid number is required.": "Please enter a valid number.",
  "A valid integer is required.": "Please enter a valid number.",
};

/** Patterns for polygon/geometry errors only. */
const POLYGON_ERROR_PATTERNS: [RegExp, string][] = [
  [/GEOS|WKTReader|closing rings/i, "Invalid polygon format. Please check the geometry and ensure all rings are properly closed."],
  [/Error updating neighbors/i, "Invalid polygon. Please verify the geometry format and closing rings."],
  [/Invalid geometry/i, "Invalid polygon format. Please verify the geometry and closing rings."],
];

function toUserFriendlyMessage(msg: string, isPolygon = false): string {
  if (USER_FRIENDLY_MESSAGES[msg]) return USER_FRIENDLY_MESSAGES[msg];
  if (isPolygon) {
    for (const [pattern, friendly] of POLYGON_ERROR_PATTERNS) {
      if (pattern.test(msg)) return friendly;
    }
  }
  return msg;
}

/** Converts backend geometry/polygon errors to user-friendly messages. */
export function toUserFriendlyPolygonError(msg: string): string {
  return toUserFriendlyMessage(msg, true);
}

export function validateSiteForm(
  formData: Record<string, unknown>,
  requiredFields: readonly string[],
  numericFields: string[] = []
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of requiredFields) {
    const value = formData[field];
    if (value == null || value === "") {
      errors[field] = "This field is required.";
    }
  }

  for (const field of numericFields) {
    const value = formData[field];
    const hasValue = value != null && value !== "";
    if (hasValue && isNaN(Number(value))) {
      errors[field] = "Please enter a valid number.";
    }
  }

  return errors;
}

export function parseBackendValidationErrors(
  error: unknown
): Record<string, string> | null {
  const err = error as { response?: { status?: number; data?: unknown } };
  const data = err?.response?.status === 400 && err?.response?.data;
  if (!data) return null;

  if (typeof data === "string")
    return { detail: toUserFriendlyMessage(data, true) };

  if (typeof data !== "object") return null;

  const obj = data as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      const raw = Array.isArray(v) ? v[0] : String(v);
      const field = k === "poly" ? "polygon" : k;
      const value = toUserFriendlyMessage(raw, field === "polygon");
      return [field, value];
    })
  );
}
